<template>
  <div class="sp-root">

    <!-- ── PAGE HEADER ── -->
    <div class="sp-header">
      <div>
        <h1 class="sp-title">Subscription</h1>
        <p class="sp-subtitle">Manage your plan and billing</p>
      </div>
    </div>

    <!-- ── CURRENT PLAN STATUS CARD ── -->
    <div class="sp-status-card" v-if="!loading">
      <div class="sp-status-left">
        <div class="sp-status-icon">{{ currentPlanMeta.icon }}</div>
        <div>
          <div class="sp-status-plan">{{ currentPlanMeta.name }} Plan</div>
          <div v-if="isAccountClosureScheduled" class="sp-status-detail sp-status-closing">
            Closing · Data archived {{ closureDate }}
          </div>
          <div v-else-if="isCancellingActive" class="sp-status-detail sp-status-cancelling">
            Cancelling · Active until {{ formattedRenewsAt }}
          </div>
          <div v-else-if="isExpired" class="sp-status-detail sp-status-expired">
            Plan expired · Upgrade to restore access
          </div>
          <div v-else-if="hasScheduledDowngrade" class="sp-status-detail sp-status-downgrading">
            Downgrading to {{ PLANS.find(p => p.key === scheduledDowngradePlan)?.name ?? scheduledDowngradePlan }} · Effective {{ scheduledDowngradeDate }}
          </div>
          <div v-else-if="subscription.renewsAt" class="sp-status-detail">
            Renews {{ formattedRenewsAt }}
          </div>
          <div v-else class="sp-status-detail">No billing — free forever</div>
        </div>
      </div>
      <div class="sp-status-right">
        <div class="sp-status-badge" :class="statusBadgeClass">{{ statusLabel }}</div>
        <button
          v-if="isCancellingActive && !isAccountClosureScheduled"
          class="sp-cancel-link sp-cancel-link-reactivate"
          :disabled="actionLoading"
          @click="confirmingReactivate = true"
        >
          Reactivate subscription
        </button>
      </div>
    </div>

    <!-- ── PLAN CARDS ── -->
    <div v-if="loading" class="sp-spinner-wrap">
      <div class="sp-spinner"></div>
    </div>

    <template v-else>
    <div class="sp-section-label">Available Plans</div>
    <div class="sp-plans-grid">
      <div
        v-for="plan in PLANS"
        :key="plan.key"
        class="sp-plan-card"
        :class="{
          'sp-plan-current':  isCurrent(plan),
          'sp-plan-locked':   isLocked(plan),
          'sp-plan-featured': plan.highlight && !isCurrent(plan) && !isLocked(plan),
        }"
      >
        <!-- Popular badge -->
        <div v-if="plan.highlight && !isCurrent(plan) && !isLocked(plan)" class="sp-popular-badge">
          Most Popular
        </div>

        <div class="sp-plan-icon">{{ plan.icon }}</div>
        <div class="sp-plan-name">{{ plan.name }}</div>
        <div class="sp-plan-price">{{ plan.price }}</div>
        <div class="sp-plan-period">{{ plan.period }}</div>

        <ul class="sp-feature-list">
          <li v-for="feature in plan.features" :key="feature" class="sp-feature-item">
            <span class="sp-feature-check" :class="isLocked(plan) || isCurrent(plan) ? 'sp-check-muted' : 'sp-check-green'">✓</span>
            {{ feature }}
          </li>
        </ul>

        <!-- CTA area -->
        <div class="sp-plan-cta">
          <!-- Current plan -->
          <div v-if="isCurrent(plan) && subscription.status !== 'cancelled'" class="sp-btn sp-btn-current">
            Current plan
          </div>
          <!-- Cancelled but still on this plan -->
          <div v-else-if="isCurrent(plan) && subscription.status === 'cancelled'" class="sp-btn sp-btn-current">
            Active until {{ formattedRenewsAt }}
          </div>
          <!-- Locked (lower non-free tier — no downgrade) -->
          <div v-else-if="isLocked(plan)" class="sp-btn sp-btn-locked">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Not available
          </div>
          <!-- Lower-tier card when subscription is already cancelling — downgrade unnecessary -->
          <div v-else-if="['pro', 'pro_plus'].includes(plan.key) && isCancellingActive" class="sp-btn sp-btn-locked">
            Not available
          </div>
          <!-- Downgrade already scheduled to this plan -->
          <div v-else-if="isDowngradeScheduled(plan)" class="sp-btn sp-btn-scheduled">
            ⬇ Scheduled from {{ scheduledDowngradeDate }}
          </div>
          <!-- All other plan changes blocked while a downgrade is in progress -->
          <div v-else-if="hasScheduledDowngrade" class="sp-btn sp-btn-locked">
            Not available
          </div>
          <!-- Downgrade available -->
          <button v-else-if="isDowngradable(plan)" class="sp-btn sp-btn-downgrade" @click="openDowngradeModal(plan.key)">
            Downgrade to {{ plan.name }} ↓
          </button>
          <!-- All changes blocked during account closure -->
          <div v-else-if="isAccountClosureScheduled" class="sp-btn sp-btn-locked">
            Not available
          </div>
          <!-- Upgrade -->
          <button v-else class="sp-btn sp-btn-upgrade" @click="initiateUpgrade(plan.key)">
            Upgrade to {{ plan.name }} ↑
          </button>
        </div>
      </div>
    </div>

    <!-- ── ERROR ── -->
    <div v-if="actionError" class="sp-error-banner">{{ actionError }}</div>

    <!-- ── SCHEDULED DOWNGRADE NOTICE ── -->
    <div v-if="hasScheduledDowngrade && subscription.status !== 'cancelled'" class="sp-cancel-section">
      <div class="sp-cancel-header">Scheduled downgrade to {{ PLANS.find(p => p.key === scheduledDowngradePlan)?.name ?? scheduledDowngradePlan }}</div>
      <p class="sp-cancel-desc">
        Your plan will change from <strong>{{ currentPlanMeta.name }}</strong> to
        <strong>{{ PLANS.find(p => p.key === scheduledDowngradePlan)?.name ?? scheduledDowngradePlan }}</strong> on <strong>{{ scheduledDowngradeDate }}</strong>.
        You can cancel this downgrade at any time before that date and any children
        marked for archival will be automatically restored.
      </p>
      <ul v-if="childrenScheduledForArchive.length" class="sp-modal-children" style="margin-bottom: 16px; text-align: left;">
        <li v-for="child in childrenScheduledForArchive" :key="child.id" class="sp-modal-child sp-modal-child-archive">
          <span class="sp-modal-child-avatar">{{ childAvatar(child.id) }}</span>
          <span>{{ child.name }}</span>
          <span class="sp-archive-date-pill">Archives {{ scheduledDowngradeDate }}</span>
        </li>
      </ul>
      <template v-if="confirmingCancelDowngrade">
        <p class="sp-cancel-confirm-text">Cancel the downgrade? Your {{ currentPlanMeta.name }} plan will continue.</p>
        <div class="sp-cancel-btns">
          <button class="sp-btn sp-btn-confirm" :disabled="actionLoading" @click="doCancelScheduledDowngrade">
            {{ actionLoading ? 'Cancelling…' : 'Yes, cancel downgrade' }}
          </button>
          <button class="sp-btn sp-btn-cancel-confirm" @click="confirmingCancelDowngrade = false">Not now</button>
        </div>
      </template>
      <button v-else class="sp-cancel-link" @click="confirmingCancelDowngrade = true">
        Cancel scheduled downgrade
      </button>
    </div>

    <!-- ── REACTIVATE SUBSCRIPTION NOTICE ── -->
    <div v-if="isCancellingActive && !isAccountClosureScheduled" class="sp-cancel-section">
      <div class="sp-cancel-header">Subscription cancelling</div>
      <p class="sp-cancel-desc">
        Your subscription has been cancelled. You still have <strong>{{ currentPlanMeta.name }}</strong>
        access until <strong>{{ formattedRenewsAt }}</strong>.
      </p>
      <button class="sp-cancel-link sp-cancel-link-reactivate" @click="confirmingReactivate = true">
        Reactivate subscription
      </button>
    </div>

    <!-- ── ACCOUNT CLOSURE NOTICE ── -->
    <div v-if="isAccountClosureScheduled" class="sp-cancel-section sp-cancel-section-red">
      <div class="sp-cancel-header">Account scheduled for closure</div>
      <p class="sp-cancel-desc">
        Your account and all child data will be permanently archived on <strong>{{ closureDate }}</strong>.
        You will no longer be able to log in after that date.
      </p>
      <button class="sp-cancel-link" @click="confirmingCancelClosure = true">
        Cancel account closure
      </button>
    </div>

    </template><!-- end v-else (loaded) -->

  </div>

  <!-- ── UPGRADE CONFIRMATION MODAL ── -->
  <Teleport to="body">
    <div v-if="confirmingUpgrade && confirmingUpgradePlan" class="sp-modal-overlay" @click.self="cancelUpgrade">
      <div class="sp-modal sp-umodal">
        <div class="sp-umodal-confetti">
          <span style="animation-delay:0s">🎉</span>
          <span style="animation-delay:0.15s">⭐</span>
          <span style="animation-delay:0.3s">🎉</span>
        </div>
        <div class="sp-umodal-badge">{{ confirmingUpgradePlan.icon }} {{ confirmingUpgradePlan.name }} Plan</div>
        <h2 class="sp-umodal-title">Upgrade to {{ confirmingUpgradePlan.name }}?</h2>
        <p class="sp-umodal-sub">Great choice — here's what you'll unlock immediately:</p>
        <ul class="sp-umodal-features">
          <li v-for="feature in confirmingUpgradePlan.features" :key="feature" class="sp-umodal-feature">
            <span class="sp-umodal-check">✓</span>{{ feature }}
          </li>
        </ul>
        <div class="sp-umodal-price">{{ confirmingUpgradePlan.price }}<span class="sp-umodal-period"> {{ confirmingUpgradePlan.period }}</span></div>
        <p class="sp-umodal-proration">You'll only be charged the pro-rated difference for the rest of this billing period.</p>
        <div class="sp-modal-actions" style="margin-top:20px">
          <button class="sp-btn sp-btn-upgrade-confirm" :disabled="actionLoading" @click="doUpgrade(confirmingUpgrade)">
            {{ actionLoading ? 'Upgrading…' : 'Confirm upgrade →' }}
          </button>
          <button class="sp-btn sp-btn-cancel-confirm" :disabled="actionLoading" @click="cancelUpgrade">Not now</button>
        </div>
        <div v-if="actionError" class="sp-modal-error">{{ actionError }}</div>
      </div>
    </div>
  </Teleport>

  <!-- ── UPGRADE SUCCESS MODAL ── -->
  <Teleport to="body">
    <div v-if="upgradeSuccess && upgradedPlan" class="sp-modal-overlay">
      <div class="sp-modal sp-umodal">
        <div class="sp-umodal-confetti">
          <span style="animation-delay:0s">🎉</span>
          <span style="animation-delay:0.15s">⭐</span>
          <span style="animation-delay:0.3s">🎉</span>
        </div>
        <div class="sp-umodal-badge sp-umodal-badge-success">{{ upgradedPlan.icon }} {{ upgradedPlan.name }} Plan — Active</div>
        <h2 class="sp-umodal-title">You're on {{ upgradedPlan.name }}!</h2>
        <p class="sp-umodal-sub">Your plan is active. Everything below is now unlocked:</p>
        <ul class="sp-umodal-features">
          <li v-for="feature in upgradedPlan.features" :key="feature" class="sp-umodal-feature">
            <span class="sp-umodal-check">✓</span>{{ feature }}
          </li>
        </ul>
        <div class="sp-modal-actions" style="margin-top:24px">
          <button class="sp-btn sp-btn-upgrade-confirm" @click="closeUpgradeSuccess">
            Go to Dashboard →
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ── DOWNGRADE MODAL ── -->
  <Teleport to="body">
    <div v-if="confirmingPlanDowngrade" class="sp-modal-overlay" @click.self="closeDowngradeModal">
      <div class="sp-modal">
        <div class="sp-modal-icon">⬇️</div>
        <h2 class="sp-modal-title">Downgrade to {{ PLANS.find(p => p.key === confirmingPlanDowngrade)?.name }}?</h2>
        <p class="sp-modal-body">
          <template v-if="subscription.renewsAt">
            Your plan will change to <strong>{{ PLANS.find(p => p.key === confirmingPlanDowngrade)?.name }}</strong> on <strong>{{ formattedRenewsAt }}</strong>,
            when your current {{ currentPlanMeta.name }} billing period ends. You'll keep {{ currentPlanMeta.name }} access until then.
          </template>
          <template v-else>
            Your plan will change to <strong>{{ PLANS.find(p => p.key === confirmingPlanDowngrade)?.name }}</strong> at the end of your current billing period.
            You'll keep {{ currentPlanMeta.name }} access until then.
          </template>
        </p>

        <!-- Need to archive children -->
        <template v-if="downgradeNeedCount > 0">
          <p class="sp-modal-body sp-modal-body-warn">
            {{ PLANS.find(p => p.key === confirmingPlanDowngrade)?.name }} allows {{ downgradeTargetMaxChildren }} {{ downgradeTargetMaxChildren === 1 ? 'child' : 'children' }}. You currently have {{ selectableChildren.length }} —
            select <strong>{{ downgradeNeedCount }}</strong> to archive:
          </p>
          <ul class="sp-child-select-list">
            <li
              v-for="child in selectableChildren"
              :key="child.id"
              class="sp-child-select-item"
              :class="{ 'sp-child-select-checked': selectedForArchive.has(child.id) }"
              @click="toggleArchiveSelection(child.id)"
            >
              <span class="sp-child-select-avatar">{{ childAvatar(child.id) }}</span>
              <span class="sp-child-select-name">{{ child.name }}</span>
              <span class="sp-child-select-tick">{{ selectedForArchive.has(child.id) ? '✓' : '' }}</span>
            </li>
          </ul>
          <p class="sp-modal-restore-note">
            Selected children will be archived at the end of your billing period<template v-if="subscription.renewsAt"> (<strong>{{ formattedRenewsAt }}</strong>)</template>.
            You can cancel this downgrade before then and they'll be fully restored.
          </p>
        </template>

        <!-- No archiving needed -->
        <template v-else>
          <p class="sp-modal-body sp-modal-body-ok">
            ✓ You have 1 or fewer children — no archival is needed.
          </p>
          <p class="sp-modal-restore-note">
            You can cancel this downgrade at any time before your billing period ends<template v-if="subscription.renewsAt"> (<strong>{{ formattedRenewsAt }}</strong>)</template>.
          </p>
        </template>

        <div class="sp-modal-actions">
          <button
            class="sp-btn sp-btn-downgrade-confirm"
            :disabled="actionLoading || !canConfirmDowngrade"
            @click="doScheduleDowngrade"
          >
            {{ actionLoading ? 'Scheduling…' : 'Confirm downgrade' }}
          </button>
          <button class="sp-btn sp-btn-cancel-confirm" :disabled="actionLoading" @click="closeDowngradeModal">
            Keep Family plan
          </button>
        </div>
        <div v-if="actionError" class="sp-modal-error">{{ actionError }}</div>
      </div>
    </div>
  </Teleport>

  <!-- ── REACTIVATE CONFIRMATION MODAL ── -->
  <Teleport to="body">
    <div v-if="confirmingReactivate" class="sp-modal-overlay" @click.self="confirmingReactivate = false">
      <div class="sp-modal sp-umodal">
        <div class="sp-umodal-confetti">
          <span style="animation-delay:0s">🎊</span>
          <span style="animation-delay:0.15s">💛</span>
          <span style="animation-delay:0.3s">🎊</span>
        </div>
        <div class="sp-umodal-badge">✨ Don't leave — you're amazing!</div>
        <h2 class="sp-umodal-title">Brilliant — let's keep this going! 🎉</h2>
        <p class="sp-umodal-sub">
          Every practice session is one step closer to that dream Grammar School place.
          Your <strong>{{ currentPlanMeta.name }}</strong> subscription will stay active — every streak,
          every mastery band, every bit of your child's hard work stays exactly where it belongs.
        </p>
        <div class="sp-modal-actions" style="margin-top:24px">
          <button class="sp-btn sp-btn-upgrade-confirm sp-btn-reactivate-confirm" :disabled="actionLoading" @click="doReactivate">
            {{ actionLoading ? 'Reactivating…' : 'Yes, keep my subscription →' }}
          </button>
          <button class="sp-btn sp-btn-cancel-confirm" :disabled="actionLoading" @click="confirmingReactivate = false">Not now</button>
        </div>
        <div v-if="actionError" class="sp-modal-error">{{ actionError }}</div>
      </div>
    </div>
  </Teleport>

  <!-- ── REACTIVATE SUCCESS MODAL ── -->
  <Teleport to="body">
    <div v-if="reactivateSuccess" class="sp-modal-overlay">
      <div class="sp-modal sp-umodal">
        <div class="sp-umodal-confetti">
          <span style="animation-delay:0s">🎉</span>
          <span style="animation-delay:0.15s">🚀</span>
          <span style="animation-delay:0.3s">🎉</span>
        </div>
        <div class="sp-umodal-badge sp-umodal-badge-success">{{ currentPlanMeta.icon }} {{ currentPlanMeta.name }} — Active</div>
        <h2 class="sp-umodal-title">Yes! You're back in action! 🚀</h2>
        <p class="sp-umodal-sub">
          Your <strong>{{ currentPlanMeta.name }}</strong> subscription is live again.
          Every session, every streak and every mastery milestone is exactly where you left it.
          Now let's get back to it — that Grammar School place won't win itself!
        </p>
        <div class="sp-modal-actions" style="margin-top:24px">
          <button class="sp-btn sp-btn-upgrade-confirm" @click="reactivateSuccess = false">
            Let's keep learning →
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ── CANCEL CLOSURE CONFIRMATION MODAL ── -->
  <Teleport to="body">
    <div v-if="confirmingCancelClosure" class="sp-modal-overlay" @click.self="confirmingCancelClosure = false">
      <div class="sp-modal sp-umodal">
        <div class="sp-umodal-confetti">
          <span style="animation-delay:0s">🎊</span>
          <span style="animation-delay:0.15s">💛</span>
          <span style="animation-delay:0.3s">🎊</span>
        </div>
        <div class="sp-umodal-badge">🙌 So glad you reconsidered!</div>
        <h2 class="sp-umodal-title">Wonderful — we're so happy you're staying! 🎉</h2>
        <p class="sp-umodal-sub">
          Your children's hard-earned progress means everything — and we'd hate to see it go.
          Cancel the closure and your <strong>{{ currentPlanMeta.name }}</strong> subscription continues
          in full, with every streak, mastery band and progress record completely safe.
        </p>
        <div class="sp-modal-actions" style="margin-top:24px">
          <button class="sp-btn sp-btn-upgrade-confirm sp-btn-reactivate-confirm" :disabled="actionLoading" @click="doCancelAccountClosure">
            {{ actionLoading ? 'Cancelling closure…' : 'Yes, keep my account →' }}
          </button>
          <button class="sp-btn sp-btn-cancel-confirm" :disabled="actionLoading" @click="confirmingCancelClosure = false">Not now</button>
        </div>
        <div v-if="actionError" class="sp-modal-error">{{ actionError }}</div>
      </div>
    </div>
  </Teleport>

  <!-- ── CANCEL CLOSURE SUCCESS MODAL ── -->
  <Teleport to="body">
    <div v-if="closureSuccess" class="sp-modal-overlay">
      <div class="sp-modal sp-umodal">
        <div class="sp-umodal-confetti">
          <span style="animation-delay:0s">🎉</span>
          <span style="animation-delay:0.15s">🌟</span>
          <span style="animation-delay:0.3s">🎉</span>
        </div>
        <div class="sp-umodal-badge sp-umodal-badge-success">{{ currentPlanMeta.icon }} {{ currentPlanMeta.name }} — Active</div>
        <h2 class="sp-umodal-title">Amazing — so glad you're with us! 🌟</h2>
        <p class="sp-umodal-sub">
          Account closure cancelled! Your <strong>{{ currentPlanMeta.name }}</strong> subscription is fully active.
          Your children can dive straight back into their learning journey — every question answered,
          every streak built and every mastery band earned is still there waiting for them!
        </p>
        <div class="sp-modal-actions" style="margin-top:24px">
          <button class="sp-btn sp-btn-upgrade-confirm" @click="closureSuccess = false">
            Back to learning →
          </button>
        </div>
      </div>
    </div>
  </Teleport>

</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { useStudentStore } from '@/stores/studentStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { useAuth } from '@/composables/useAuth';
import { accountApi } from '@/services/accountApi';
import * as authService from '@/services/authService';
import { getPlans, PLAN_ICONS, PLAN_HIGHLIGHT, formatPrice, formatPeriod } from '@/services/planService';
import { useAnalytics } from '@/composables/useAnalytics';

const router = useRouter();
const route  = useRoute();
const userStore = useUserStore();
const studentStore = useStudentStore();
const preferencesStore = usePreferencesStore();
const { session } = useAuth();
const { track } = useAnalytics();

function childAvatar(childId) {
  return preferencesStore.getAvatar(childId) || '🧒';
}

// ── State ────────────────────────────────────────────────────────────────────
const loading           = ref(true);
const actionLoading     = ref(false);
const actionError       = ref('');
const confirmingUpgrade        = ref(null);    // plan key being confirmed for upgrade
const upgradeSuccess           = ref(null);    // plan key after successful upgrade
const confirmingDowngrade      = ref(false);   // cancel-subscription modal
const confirmingPlanDowngrade  = ref(null);    // plan key for downgrade-to-pro modal
const confirmingCancelDowngrade = ref(false);  // confirm cancel-scheduled-downgrade
const confirmingCancelClosure  = ref(false);  // confirm cancel-account-closure
const confirmingReactivate     = ref(false);  // confirm reactivate subscription
const reactivateSuccess        = ref(false);  // show reactivate success modal
const closureSuccess           = ref(false);  // show cancel-closure success modal
const selectedForArchive       = ref(new Set()); // childIds checked in downgrade modal

// Plan documents from the API (planId-keyed)
const apiPlans = ref({});

// Full subscription object fetched from API
const subscription = ref({
  plan: session.value?.plan || 'free',
  planTier: 0,
  status: 'active',
  renewsAt: null,
  cancelledAt: null,
});

// ── Derived ──────────────────────────────────────────────────────────────────

/** Plan cards sorted by tier, built from API data + UI-only metadata. */
const PLANS = computed(() =>
  Object.values(apiPlans.value)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map(p => ({
      key:       p.planId,
      tier:      p.sortOrder ?? 0,
      icon:      PLAN_ICONS[p.planId] ?? '📋',
      name:      p.name,
      price:     formatPrice(p),
      period:    formatPeriod(p),
      highlight: PLAN_HIGHLIGHT === p.planId,
      features:  p.displayFeatures ?? [],
    }))
);

// Use the server-computed effectivePlan as the source of truth for what the
// user currently has access to. When a cancelled subscription's renewsAt has
// passed, effectivePlan='free' — the user is treated as a new free subscriber.
const currentPlan     = computed(() => subscription.value.effectivePlan || subscription.value.plan || 'free');
const currentTier     = computed(() => PLANS.value.find(p => p.key === currentPlan.value)?.tier ?? 0);
const currentPlanMeta = computed(() =>
  PLANS.value.find(p => p.key === currentPlan.value) ||
  { icon: PLAN_ICONS[currentPlan.value] ?? '🌱', name: currentPlan.value }
);

// True when the subscription is cancelled but the paid period is still running
const isCancellingActive = computed(() =>
  subscription.value.status === 'cancelled' && currentPlan.value !== 'free'
);
// True when the subscription has expired (cancelled + renewsAt in the past)
const isExpired = computed(() =>
  subscription.value.status === 'cancelled' && currentPlan.value === 'free'
);

function isCurrent(plan) { return plan.key === currentPlan.value; }
function isLocked(plan)  {
  // Lower-tier paid plans are never fully locked when user can downgrade to them
  const downgradePaths = { family: ['pro_plus', 'pro'], pro_plus: ['pro'] };
  if ((downgradePaths[currentPlan.value] || []).includes(plan.key)) return false;
  return plan.tier < currentTier.value;
}
// Show "Downgrade ↓" button: lower-tier paid plans when user can downgrade to them,
// no downgrade already scheduled, and subscription is not already cancelling.
function isDowngradable(plan) {
  const downgradePaths = {
    family:   ['pro_plus', 'pro'],
    pro_plus: ['pro'],
  };
  return (downgradePaths[currentPlan.value] || []).includes(plan.key)
    && !hasScheduledDowngrade.value
    && subscription.value.status !== 'cancelled';
}
// Show "Scheduled from [date]": the plan that is already scheduled for downgrade
function isDowngradeScheduled(plan) {
  return hasScheduledDowngrade.value && scheduledDowngradePlan.value === plan.key;
}

// ── Account closure ───────────────────────────────────────────────────────────
const isAccountClosureScheduled = computed(() => !!subscription.value.scheduledClosure);
const closureDate = computed(() => {
  const d = subscription.value.scheduledClosure?.effectiveAt;
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
});

const statusLabel = computed(() => {
  if (isAccountClosureScheduled.value) return 'Closing';
  if (isCancellingActive.value) return 'Cancelling';
  if (isExpired.value)          return 'Expired';
  if (subscription.value.status === 'trial') return 'Trial';
  return 'Active';
});
const statusBadgeClass = computed(() => ({
  'sp-badge-active':    !isAccountClosureScheduled.value && !isCancellingActive.value && !isExpired.value && subscription.value.status !== 'trial',
  'sp-badge-closing':   isAccountClosureScheduled.value,
  'sp-badge-cancelled': !isAccountClosureScheduled.value && isCancellingActive.value,
  'sp-badge-expired':   isExpired.value,
  'sp-badge-trial':     subscription.value.status === 'trial',
}));

const formattedRenewsAt = computed(() => {
  const d = subscription.value.renewsAt;
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
});


// Plan objects for the upgrade modals
const confirmingUpgradePlan = computed(() => PLANS.value.find(p => p.key === confirmingUpgrade.value) || null);
const upgradedPlan          = computed(() => PLANS.value.find(p => p.key === upgradeSuccess.value)    || null);

// ── Scheduled downgrade ──────────────────────────────────────────────────────
const hasScheduledDowngrade  = computed(() => !!subscription.value.scheduledDowngrade);
const scheduledDowngradePlan = computed(() => subscription.value.scheduledDowngrade?.plan || null);
const scheduledDowngradeDate = computed(() => {
  const d = subscription.value.scheduledDowngrade?.effectiveAt;
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
});

// Children whose scheduledArchiveAt is set (display in manage section)
const childrenScheduledForArchive = computed(() =>
  studentStore.students.filter(c => c.scheduledArchiveAt)
);
// Children available to select for archival in the downgrade modal
const selectableChildren = computed(() =>
  studentStore.students.filter(c => !c.scheduledArchiveAt)
);
// How many children exceed the target plan's child limit
const downgradeTargetMaxChildren = computed(() => {
  const target = PLANS.value.find(p => p.key === confirmingPlanDowngrade.value);
  // Get maxChildren from apiPlans data; default to 1 if unknown
  return apiPlans.value[target?.key]?.limits?.maxChildren ?? 1;
});
const downgradeNeedCount = computed(() =>
  Math.max(0, selectableChildren.value.length - downgradeTargetMaxChildren.value)
);
const canConfirmDowngrade = computed(() =>
  downgradeNeedCount.value === 0 || selectedForArchive.value.size >= downgradeNeedCount.value
);

// ── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(async () => {
  const uid = session.value?.uid;
  if (!uid) { loading.value = false; return; }
  try {
    const [{ account }, plans] = await Promise.all([
      accountApi.getAccount(uid),
      getPlans(),
      studentStore.loadChildren(uid),
    ]);
    if (account?.subscription) subscription.value = account.subscription;
    if (Object.keys(plans).length) apiPlans.value = plans;

    // Show the success modal when returning from Stripe Checkout
    if (route.query.stripe_upgraded === '1' && account?.subscription?.plan) {
      upgradeSuccess.value = account.subscription.plan;
      router.replace({ query: {} }); // clean the URL without a page reload
    }
  } catch (e) {
    console.warn('[SubscriptionPage] Failed to load:', e);
  } finally {
    loading.value = false;
  }
});

// ── Actions ──────────────────────────────────────────────────────────────────
function initiateUpgrade(planKey) {
  confirmingUpgrade.value = planKey;
  track('upgrade_initiated', { from_plan: currentPlan.value, to_plan: planKey });
}

function cancelUpgrade() {
  track('upgrade_abandoned', { from_plan: currentPlan.value, to_plan: confirmingUpgrade.value });
  confirmingUpgrade.value = null;
  actionError.value = '';
}

function closeUpgradeSuccess() {
  upgradeSuccess.value = null;
  router.push('/parent/dashboard');
}

async function doUpgrade(newPlan) {
  actionError.value   = '';
  actionLoading.value = true;
  try {
    const uid = session.value?.uid;

    if (subscription.value.externalSubscriptionId) {
      // User already has a Stripe subscription — modify it in-place.
      // Stripe prorates the charge; no second subscription is created.
      await accountApi.upgradeSubscription({ uid, plan: newPlan });
      const { account } = await accountApi.getAccount(uid);
      subscription.value = account.subscription;
      authService.setSubscription(uid, account.subscription);
      userStore.session = authService.getSession();
      track('upgrade_completed', { from_plan: currentPlan.value, to_plan: newPlan });
      confirmingUpgrade.value = null;
      upgradeSuccess.value = newPlan;
    } else {
      // No existing subscription — start a fresh Stripe Checkout Session.
      const origin = window.location.origin;
      const { url } = await accountApi.createCheckoutSession({
        uid,
        plan:       newPlan,
        successUrl: `${origin}/parent/subscription?stripe_upgraded=1`,
        cancelUrl:  `${origin}/parent/subscription`,
      });
      track('upgrade_initiated', { from_plan: currentPlan.value, to_plan: newPlan });
      window.location.href = url;
      return; // actionLoading stays true — we're navigating away
    }
  } catch (e) {
    actionError.value = e.data?.error || e.message || 'Upgrade failed. Please try again.';
  } finally {
    actionLoading.value = false;
  }
}


// ── Downgrade modal helpers ──────────────────────────────────────────────────
function openDowngradeModal(planKey) {
  confirmingPlanDowngrade.value = planKey;
  selectedForArchive.value = new Set();
  actionError.value = '';
}
function closeDowngradeModal() {
  confirmingPlanDowngrade.value = null;
  selectedForArchive.value = new Set();
}
function toggleArchiveSelection(childId) {
  const s = new Set(selectedForArchive.value);
  if (s.has(childId)) s.delete(childId);
  else s.add(childId);
  selectedForArchive.value = s;
}

async function doScheduleDowngrade() {
  actionError.value  = '';
  actionLoading.value = true;
  try {
    const uid = session.value?.uid;
    const targetPlanKey = confirmingPlanDowngrade.value;
    await accountApi.scheduleDowngrade(uid, targetPlanKey, [...selectedForArchive.value]);
    const { account } = await accountApi.getAccount(uid);
    subscription.value = account.subscription;
    authService.setSubscription(uid, account.subscription);
    userStore.session = authService.getSession();
    await studentStore.loadChildren(uid);
    closeDowngradeModal();
  } catch (e) {
    actionError.value = e.data?.error || 'Failed to schedule downgrade. Please try again.';
  } finally {
    actionLoading.value = false;
  }
}

async function doCancelScheduledDowngrade() {
  actionError.value  = '';
  actionLoading.value = true;
  try {
    const uid = session.value?.uid;
    await accountApi.cancelScheduledDowngrade(uid);
    const { account } = await accountApi.getAccount(uid);
    subscription.value = account.subscription;
    authService.setSubscription(uid, account.subscription);
    userStore.session = authService.getSession();
    await studentStore.loadChildren(uid);
    confirmingCancelDowngrade.value = false;
  } catch (e) {
    actionError.value = e.data?.error || 'Failed to cancel downgrade. Please try again.';
  } finally {
    actionLoading.value = false;
  }
}

async function doReactivate() {
  actionError.value  = '';
  actionLoading.value = true;
  try {
    const uid = session.value?.uid;
    await accountApi.reactivateSubscription(uid);
    const { account } = await accountApi.getAccount(uid);
    subscription.value = account.subscription;
    authService.setSubscription(uid, account.subscription);
    userStore.session = authService.getSession();
    confirmingReactivate.value = false;
    reactivateSuccess.value = true;
  } catch (e) {
    actionError.value = e.data?.error || 'Reactivation failed. Please try again.';
  } finally {
    actionLoading.value = false;
  }
}

async function doCancelAccountClosure() {
  actionError.value  = '';
  actionLoading.value = true;
  try {
    const uid = session.value?.uid;
    await accountApi.cancelAccountClosure(uid);
    const { account } = await accountApi.getAccount(uid);
    subscription.value = account.subscription;
    authService.setSubscription(uid, account.subscription);
    userStore.session = authService.getSession();
    await studentStore.loadChildren(uid);
    confirmingCancelClosure.value = false;
    closureSuccess.value = true;
  } catch (e) {
    actionError.value = e.data?.error || 'Failed to cancel account closure. Please try again.';
  } finally {
    actionLoading.value = false;
  }
}
</script>

<style>
.sp-root {
  width: 100%;
  padding-bottom: 48px;
}

/* ── Header ── */
.sp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
}
.sp-title {
  font-size: 24px;
  font-weight: 800;
  color: #1A2332;
  margin: 0 0 4px;
}
.sp-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* ── Current plan status card ── */
.sp-status-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 32px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.sp-status-left {
  display: flex;
  align-items: center;
  gap: 16px;
}
.sp-status-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}
.sp-status-plan {
  font-size: 17px;
  font-weight: 700;
  color: #1A2332;
}
.sp-status-detail {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}
.sp-status-cancelling  { color: #f59e0b; }
.sp-status-closing     { color: #dc2626; }
.sp-status-expired     { color: #b91c1c; }
.sp-status-downgrading { color: #8b5cf6; }

.sp-status-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}
.sp-status-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 20px;
  letter-spacing: 0.03em;
}
.sp-badge-active    { background: #dcfce7; color: #16a34a; }
.sp-badge-closing   { background: #fee2e2; color: #b91c1c; }
.sp-badge-cancelled { background: #fef3c7; color: #92400e; }
.sp-badge-expired   { background: #fee2e2; color: #b91c1c; }
.sp-badge-trial     { background: #e0e7ff; color: #4338ca; }

/* ── Section label ── */
.sp-section-label {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 16px;
}

/* ── Plan grid ── */
.sp-plans-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 40px;
}
@media (max-width: 700px) {
  .sp-plans-grid { grid-template-columns: 1fr; }
}

/* ── Plan card ── */
.sp-plan-card {
  position: relative;
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  border: 2px solid transparent;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: box-shadow 0.15s;
}
.sp-plan-card:hover:not(.sp-plan-locked):not(.sp-plan-current) {
  box-shadow: 0 4px 16px rgba(0,0,0,0.10);
}
.sp-plan-current {
  border-color: #F59E0B;
  background: #fffbeb;
}
.sp-plan-locked {
  opacity: 0.5;
}
.sp-plan-featured {
  border-color: #6366f1;
}
.sp-spinner-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 64px 0;
}
.sp-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e2e8f0;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: sp-spin 0.7s linear infinite;
}
@keyframes sp-spin {
  to { transform: rotate(360deg); }
}

.sp-popular-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #6366f1;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 20px;
  white-space: nowrap;
  letter-spacing: 0.04em;
}
.sp-plan-icon   { font-size: 28px; margin-bottom: 4px; }
.sp-plan-name   { font-size: 18px; font-weight: 800; color: #1A2332; }
.sp-plan-price  { font-size: 26px; font-weight: 800; color: #6366f1; margin-top: 4px; }
.sp-plan-current .sp-plan-price { color: #d97706; }
.sp-plan-period { font-size: 11px; color: #94a3b8; margin-bottom: 12px; }

.sp-feature-list {
  list-style: none;
  padding: 0;
  margin: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sp-feature-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #475569;
}
.sp-feature-check    { font-size: 12px; margin-top: 1px; flex-shrink: 0; }
.sp-check-green      { color: #16a34a; }
.sp-check-muted      { color: #94a3b8; }

/* ── CTA buttons ── */
.sp-plan-cta { margin-top: 20px; }
.sp-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
}
.sp-btn-current  { background: #fef3c7; color: #92400e; cursor: default; }
.sp-btn-locked   { background: #f1f5f9; color: #94a3b8; cursor: default; }
.sp-btn-upgrade  {
  background: #1A2332;
  color: white;
  cursor: pointer;
}
.sp-btn-upgrade:hover { background: #2d3f57; }
.sp-plan-featured .sp-btn-upgrade { background: #6366f1; }
.sp-plan-featured .sp-btn-upgrade:hover { background: #4f46e5; }
.sp-btn-confirm {
  background: #16a34a;
  color: white;
  flex: 1;
}
.sp-btn-confirm:hover:not(:disabled) { background: #15803d; }
.sp-btn-confirm:disabled { opacity: 0.6; cursor: not-allowed; }
.sp-btn-cancel-confirm {
  background: #f1f5f9;
  color: #475569;
  flex: 1;
  border: none;
}
.sp-btn-cancel-confirm:hover { background: #e2e8f0; }
.sp-confirm-text {
  font-size: 13px;
  font-weight: 600;
  color: #1A2332;
  margin-bottom: 8px;
  text-align: center;
}
.sp-confirm-btns { display: flex; gap: 8px; }

/* ── Error banner ── */
.sp-error-banner {
  background: #fef2f2;
  border: 1px solid #fca5a5;
  color: #b91c1c;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 13px;
  margin-bottom: 24px;
}

/* ── Cancel section ── */
.sp-cancel-section {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  border-left: 4px solid #f59e0b;
}
.sp-cancel-header {
  font-size: 15px;
  font-weight: 700;
  color: #1A2332;
  margin-bottom: 8px;
}
.sp-cancel-desc {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 16px;
  line-height: 1.6;
}
.sp-cancel-link {
  background: none;
  border: none;
  color: #ef4444;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.sp-cancel-link:hover { color: #b91c1c; }
.sp-cancel-link-reactivate { color: #16a34a; }
.sp-cancel-link-reactivate:hover { color: #15803d; }
.sp-btn-reactivate-confirm { background: #16a34a !important; }
.sp-btn-reactivate-confirm:hover:not(:disabled) { background: #15803d !important; }
.sp-cancel-link-close { color: #94a3b8; font-size: 12px; }
.sp-cancel-link-close:hover { color: #64748b; }
.sp-cancel-section-red { border-left-color: #ef4444; }
.sp-cancel-panel-close-note {
  font-size: 12px;
  color: #94a3b8;
  margin: 0 0 8px;
  line-height: 1.5;
}

/* ── Collapsed cancel disclosure ── */
.sp-cancel-collapsed {
  margin-top: 8px;
}
.sp-cancel-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
}
.sp-cancel-toggle:hover { color: #64748b; }
.sp-cancel-panel {
  margin-top: 12px;
  padding: 16px 20px;
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 10px;
}
.sp-cancel-panel-desc {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 12px;
  line-height: 1.6;
}
.sp-cancel-confirm-text {
  font-size: 14px;
  font-weight: 600;
  color: #1A2332;
  margin-bottom: 12px;
}
.sp-cancel-btns { display: flex; gap: 10px; }
.sp-btn-cancel-confirm-danger {
  background: #ef4444;
  color: white;
  flex: 1;
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.sp-btn-cancel-confirm-danger:hover:not(:disabled) { background: #dc2626; }
.sp-btn-cancel-confirm-danger:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Cancel modal ── */
.sp-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
}
.sp-modal {
  background: white;
  border-radius: 20px;
  padding: 36px 32px;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  text-align: center;
}
.sp-modal-icon { font-size: 44px; margin-bottom: 12px; }
.sp-modal-title {
  font-size: 22px;
  font-weight: 800;
  color: #1A2332;
  margin: 0 0 12px;
}
.sp-modal-body {
  font-size: 14px;
  color: #475569;
  margin-bottom: 16px;
  line-height: 1.6;
}
.sp-modal-children {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sp-modal-child {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  color: #7f1d1d;
}
.sp-modal-child-avatar { font-size: 18px; }
.sp-modal-warning {
  font-size: 13px;
  color: #b91c1c;
  background: #fef2f2;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 12px;
  line-height: 1.5;
}
.sp-modal-hook {
  font-size: 13px;
  color: #64748b;
  font-style: italic;
  margin-bottom: 24px;
  line-height: 1.5;
}
.sp-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sp-btn-downgrade-confirm {
  background: #dc2626;
  color: white;
  border: none;
  flex: none;
  width: 100%;
}
.sp-btn-downgrade-confirm:hover:not(:disabled) { background: #b91c1c; }
.sp-btn-downgrade-confirm:disabled { opacity: 0.5; cursor: not-allowed; }
.sp-modal-error {
  margin-top: 12px;
  font-size: 13px;
  color: #b91c1c;
}

/* ── Downgrade button + scheduled badge ── */
.sp-btn-downgrade {
  background: #f3f4f6;
  color: #374151;
  border: 1.5px solid #d1d5db;
  cursor: pointer;
}
.sp-btn-downgrade:hover { background: #e5e7eb; border-color: #9ca3af; }
.sp-btn-scheduled {
  background: #f3f0ff;
  color: #7c3aed;
  border: 1.5px solid #c4b5fd;
  cursor: default;
  font-size: 12px;
}

/* ── Cancel section divider between downgrade and cancel sub-sections ── */
.sp-cancel-divider {
  height: 1px;
  background: #f1f5f9;
  margin: 20px 0;
}

/* ── Archive date pill in scheduled-for-archival list ── */
.sp-modal-child-archive {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #92400e;
}
.sp-archive-date-pill {
  margin-left: auto;
  font-size: 11px;
  font-weight: 700;
  background: #fef3c7;
  color: #92400e;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
}

/* ── Downgrade modal: child selection list ── */
.sp-child-select-list {
  list-style: none;
  padding: 0;
  margin: 0 0 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
}
.sp-child-select-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
}
.sp-child-select-item:hover { background: #f1f5f9; border-color: #cbd5e1; }
.sp-child-select-checked {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #7f1d1d;
}
.sp-child-select-avatar { font-size: 18px; }
.sp-child-select-name   { flex: 1; }
.sp-child-select-tick   {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fca5a5;
  color: #7f1d1d;
  font-size: 12px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sp-child-select-checked .sp-child-select-tick { background: #dc2626; color: white; }

/* ── Downgrade modal body variants ── */
.sp-modal-body-warn { color: #92400e; background: #fff7ed; border-radius: 6px; padding: 8px 12px; margin-top: -4px; font-size: 13px; }
.sp-modal-body-ok   { color: #166534; background: #f0fdf4; border-radius: 6px; padding: 8px 12px; margin-top: -4px; font-size: 13px; }
.sp-modal-restore-note {
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
  margin-bottom: 20px;
  line-height: 1.5;
}

/* ── Upgrade confirmation & success modals ── */
.sp-umodal {
  max-width: 480px;
  text-align: center;
  padding: 36px 32px 32px;
  animation: sp-umodal-in 0.3s ease both;
}
@keyframes sp-umodal-in {
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
.sp-umodal-confetti {
  font-size: 26px;
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 14px;
}
.sp-umodal-confetti span {
  display: inline-block;
  animation: sp-bounce 0.5s ease both;
}
@keyframes sp-bounce {
  0%   { opacity: 0; transform: scale(0.4) translateY(-8px); }
  60%  { transform: scale(1.2) translateY(2px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}
.sp-umodal-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fef3c7;
  color: #92400e;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 5px 16px;
  border-radius: 100px;
  border: 1px solid #fcd34d;
  margin-bottom: 14px;
}
.sp-umodal-badge-success {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}
.sp-umodal-title {
  font-size: 24px;
  font-weight: 800;
  color: #1A2332;
  margin: 0 0 8px;
  line-height: 1.2;
}
.sp-umodal-sub {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 18px;
  line-height: 1.6;
}
.sp-umodal-features {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
}
.sp-umodal-feature {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: #374151;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 9px 12px;
}
.sp-umodal-check {
  color: #16a34a;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}
.sp-umodal-price {
  font-size: 24px;
  font-weight: 800;
  color: #6366f1;
  margin-bottom: 4px;
}
.sp-umodal-period {
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
}
.sp-umodal-proration {
  font-size: 12px;
  color: #94a3b8;
  font-style: italic;
  margin: 0 0 4px;
  line-height: 1.5;
}
.sp-btn-upgrade-confirm {
  background: #6366f1;
  color: white;
  border: none;
  width: 100%;
  padding: 13px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
}
.sp-btn-upgrade-confirm:hover:not(:disabled) { background: #4f46e5; transform: translateY(-1px); }
.sp-btn-upgrade-confirm:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
